import { DiscoveryApi } from '@backstage/core-plugin-api';

import { McpGardenApi } from '../../api/mcpgarden.api';
import {
  McpAccessRequest,
  McpAccessRequestBulkUpdateRequest,
  McpAccessRequestBulkUpdateResponse,
  McpAccessRequestCreateRequest,
  McpAccessRequestCreateResponse,
  McpAccessRequestListResponse,
  McpAccessRequestUpdateRequest,
  McpAccessRequestUpdateResponse,
  McpServerCreateRequest,
  McpServerCreateResponse,
  McpServerDetailed,
  McpServerListResponse,
  McpServerNameAvailabilityResponse,
  McpServerNotFoundError,
  McpServerReduced,
  McpServerUpdateRequest,
  McpServerUpdateResponse,
} from '../../api/mcpgarden.objects';

// ---------------------------------------------------------------------------------------------------- //
// Constants
// ---------------------------------------------------------------------------------------------------- //

const CONTENT_TYPE = 'application/json';
const PROXY_PLUGIN_ID = 'aiecosystem';

/** Paths of the MCP Garden endpoints, relative to the AI Ecosystem proxy base URL. */
export const MCPGardenEndpoints = {
  mcpServers: '/mcp-servers',
  availability: '/mcp-servers/availability',
  accessRequests: '/access-requests',
} as const;

/**
 * Builds the error thrown for a non-2xx response.
 * @param response - The failed fetch response
 * @param action - Human readable description of the attempted operation
 * @returns An error carrying status code, status text and the attempted action
 */
function buildRequestError(response: Response, action: string): Error {
  return new Error(
    `Error - Statuscode ${response.status} - ${response.statusText} - ${action}`,
  );
}

// ---------------------------------------------------------------------------------------------------- //
// Service Implementation
// ---------------------------------------------------------------------------------------------------- //

/**
 * Service to interact with the MCP Garden backend API.
 */
export class McpGardenService implements McpGardenApi {
  private readonly discoveryApi: DiscoveryApi;

  constructor(discoveryApi: DiscoveryApi) {
    this.discoveryApi = discoveryApi;
  }

  /** @inheritdoc */
  async getMcpServers(limit?: number): Promise<McpServerListResponse> {
    const mcpServers: McpServerReduced[] = [];
    let nextFirstItemKey: string | undefined = undefined;
    let isDevModeAuthorized: boolean | undefined = undefined;

    do {
      const page: McpServerListResponse = await this.getMcpServersPage(
        limit,
        nextFirstItemKey,
      );

      mcpServers.push(...page.mcpServers);
      nextFirstItemKey = page.lastItemKey;

      if (page.isDevModeAuthorized !== undefined) {
        isDevModeAuthorized = page.isDevModeAuthorized;
      }
    } while (nextFirstItemKey);

    return { mcpServers, isDevModeAuthorized };
  }

  /** @inheritdoc */
  async getMcpServer(mcpServerId: string): Promise<McpServerDetailed> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);

    const response = await fetch(
      `${proxyUrl}${MCPGardenEndpoints.mcpServers}/${encodeURIComponent(
        mcpServerId,
      )}`,
      {
        method: 'GET',
        headers: { Accept: CONTENT_TYPE },
      },
    );

    if (response.status === 404) {
      throw new McpServerNotFoundError(mcpServerId);
    }

    if (!response.ok) {
      throw buildRequestError(response, 'Failed to fetch MCP server.');
    }

    return (await response.json()) as McpServerDetailed;
  }

  /** @inheritdoc */
  async checkMcpServerNameAvailability(
    name: string,
    excludeMcpServerId?: string,
  ): Promise<McpServerNameAvailabilityResponse> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);
    const queryParams = new URLSearchParams();
    queryParams.append('name', name);

    if (excludeMcpServerId) {
      queryParams.append('exclude_mcp_server_id', excludeMcpServerId);
    }

    const response = await fetch(
      `${proxyUrl}${MCPGardenEndpoints.availability}?${queryParams.toString()}`,
      {
        method: 'GET',
        headers: { Accept: CONTENT_TYPE },
      },
    );

    if (!response.ok) {
      throw buildRequestError(
        response,
        'Failed to check MCP server name availability.',
      );
    }

    const data = (await response.json()) as McpServerNameAvailabilityResponse;
    return { available: data.available };
  }

  /** @inheritdoc */
  async createMcpServer(
    request: McpServerCreateRequest,
  ): Promise<McpServerCreateResponse> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);

    const response = await fetch(
      `${proxyUrl}${MCPGardenEndpoints.mcpServers}`,
      {
        method: 'POST',
        headers: { Accept: CONTENT_TYPE, 'Content-Type': CONTENT_TYPE },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw buildRequestError(response, 'Failed to create MCP server.');
    }

    return (await response.json()) as McpServerCreateResponse;
  }

  /** @inheritdoc */
  async updateMcpServer(
    mcpServerId: string,
    request: McpServerUpdateRequest,
  ): Promise<McpServerUpdateResponse> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);

    const response = await fetch(
      `${proxyUrl}${MCPGardenEndpoints.mcpServers}/${encodeURIComponent(
        mcpServerId,
      )}`,
      {
        method: 'PUT',
        headers: { Accept: CONTENT_TYPE, 'Content-Type': CONTENT_TYPE },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw buildRequestError(response, 'Failed to update MCP server.');
    }

    return (await response.json()) as McpServerUpdateResponse;
  }

  /** @inheritdoc */
  async createMcpAccessRequest(
    mcpServerId: string,
    request: McpAccessRequestCreateRequest,
  ): Promise<McpAccessRequestCreateResponse> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);

    const response = await fetch(
      `${proxyUrl}${MCPGardenEndpoints.mcpServers}/${encodeURIComponent(
        mcpServerId,
      )}${MCPGardenEndpoints.accessRequests}`,
      {
        method: 'POST',
        headers: { Accept: CONTENT_TYPE, 'Content-Type': CONTENT_TYPE },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw buildRequestError(response, 'Failed to create access request.');
    }

    return (await response.json()) as McpAccessRequestCreateResponse;
  }

  /** @inheritdoc */
  async getMcpAccessRequests(mcpServerId: string): Promise<McpAccessRequest[]> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);

    const response = await fetch(
      `${proxyUrl}${MCPGardenEndpoints.mcpServers}/${encodeURIComponent(
        mcpServerId,
      )}${MCPGardenEndpoints.accessRequests}`,
      {
        method: 'GET',
        headers: { Accept: CONTENT_TYPE },
      },
    );

    if (!response.ok) {
      throw buildRequestError(response, 'Failed to fetch access requests.');
    }

    const data = (await response.json()) as McpAccessRequestListResponse;
    return data.requests ?? [];
  }

  /** @inheritdoc */
  async updateMcpAccessRequests(
    mcpServerId: string,
    request: McpAccessRequestBulkUpdateRequest,
  ): Promise<McpAccessRequestBulkUpdateResponse> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);

    const response = await fetch(
      `${proxyUrl}${MCPGardenEndpoints.mcpServers}/${encodeURIComponent(
        mcpServerId,
      )}${MCPGardenEndpoints.accessRequests}`,
      {
        method: 'PUT',
        headers: { Accept: CONTENT_TYPE, 'Content-Type': CONTENT_TYPE },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw buildRequestError(response, 'Failed to update access request(s).');
    }

    return (await response.json()) as McpAccessRequestBulkUpdateResponse;
  }

  /** @inheritdoc */
  async unsubscribeFromMcpServer(
    mcpServerId: string,
    requestId: string,
    request: McpAccessRequestUpdateRequest,
  ): Promise<McpAccessRequestUpdateResponse> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);

    const response = await fetch(
      `${proxyUrl}${MCPGardenEndpoints.mcpServers}/${encodeURIComponent(
        mcpServerId,
      )}${MCPGardenEndpoints.accessRequests}/${encodeURIComponent(requestId)}`,
      {
        method: 'PUT',
        headers: { Accept: CONTENT_TYPE, 'Content-Type': CONTENT_TYPE },
        body: JSON.stringify(request),
      },
    );

    if (!response.ok) {
      throw buildRequestError(response, 'Failed to update access request.');
    }

    return (await response.json()) as McpAccessRequestUpdateResponse;
  }

  /** @inheritdoc */
  async deprecateMcpServer(
    mcpServerId: string,
    formData: {
      mcpServerName: string;
      deprecationDurationDays: number | null;
      deprecationReason: string;
      alternatives: string;
    },
  ): Promise<{
    mcpServerId: string;
    mcpServerName: string;
    status: string;
    deprecationStartedAt: string;
    deprecationCompletesAt: string;
    message: string;
  }> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);

    const response = await fetch(
      `${proxyUrl}${MCPGardenEndpoints.mcpServers}/${mcpServerId}/deprecate`,
      {
        method: 'POST',
        headers: {
          Accept: CONTENT_TYPE,
          'Content-Type': CONTENT_TYPE,
        },
        body: JSON.stringify(formData),
      },
    );
    if (!response.ok) {
      throw new Error(
        `Error - Statuscode ${response.status} - ${response.statusText} - Failed to deprecate agent.`,
      );
    }
    if (!response.ok) {
      throw buildRequestError(response, 'Failed to deprecate MCP server.');
    }

    return await response.json();
  }

  /** @inheritdoc */
  private async getMcpServersPage(
    limit?: number,
    firstItemKey?: string,
  ): Promise<McpServerListResponse> {
    const proxyUrl = await this.discoveryApi.getBaseUrl(PROXY_PLUGIN_ID);
    const queryParams = new URLSearchParams();

    if (limit !== undefined) {
      queryParams.append('limit', limit.toString());
    }

    if (firstItemKey) {
      queryParams.append('firstItemKey', firstItemKey);
    }

    const queryString = queryParams.toString();
    const url = queryString
      ? `${proxyUrl}${MCPGardenEndpoints.mcpServers}?${queryString}`
      : `${proxyUrl}${MCPGardenEndpoints.mcpServers}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: { Accept: CONTENT_TYPE },
    });

    if (!response.ok) {
      throw buildRequestError(response, 'Failed to fetch MCP servers.');
    }

    const data = (await response.json()) as McpServerListResponse;

    return {
      mcpServers: data.mcpServers ?? [],
      lastItemKey: data.lastItemKey,
      isDevModeAuthorized: data.isDevModeAuthorized,
    };
  }
}
